import { Calendar } from "./Calendar";
import { SessionRecord } from "./RecordEditor";
import { useEffect, useState } from "react";
import type { ActiveSession, NextWorkout } from "../shared/types";
import { api } from "./api";
import {
  Card,
  ErrorNotice,
  formatDate,
  formatDuration,
  Spinner,
  SuccessNotice,
} from "./components";

export function Home({
  onWorkout, profileId, timezone,
}: {
  onWorkout: (session?: ActiveSession) => void;
  profileId: string; timezone: string;
}) {
  const [unfinished, setUnfinished] = useState<Array<{id: string; template: ActiveSession["template"]; status: string; progress: {completed: number; prescribed: number; percentage: number}}>>([]);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<NextWorkout | null>(null);
  const [active, setActive] = useState<ActiveSession | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() {
    const [next, session, unfinishedRows] = await Promise.all([
      api<NextWorkout>("/workouts/next"),
      api<ActiveSession | undefined>("/sessions/active"),
      api<typeof unfinished>("/sessions/unfinished"),
    ]);
    setData(next);
    setActive(session ?? null);
    setUnfinished(unfinishedRows ?? []);
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  async function start(
    templateId: string,
    scheduledFor: string | undefined,
    advanceRotation = true,
  ) {
    setBusy(true);
    setError("");
    try {
      const session = await api<ActiveSession>("/sessions", {
        method: "POST",
        body: JSON.stringify({ templateId, scheduledFor, advanceRotation }),
      });
      onWorkout(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start workout");
    } finally {
      setBusy(false);
    }
  }
  async function skipToday() {
    setBusy(true);
    setError("");
    try {
      await api("/schedule/skip-today", { method: "POST", body: "{}" });
      setSuccess("Today was skipped. Your A–D rotation did not advance.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not skip today");
    } finally {
      setBusy(false);
    }
  }
  async function useLastAsNext() {
    if (!data?.lastSession?.templateId) return;
    setBusy(true);
    setError("");
    try {
      await api("/workouts/use-as-next", {
        method: "POST",
        body: JSON.stringify({ templateId: data.lastSession.templateId }),
      });
      setSuccess(
        `Workout ${data.lastSession.code} is now next in your rotation.`,
      );
      await load();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not update the rotation",
      );
    } finally {
      setBusy(false);
    }
  }
  if (!data && !error) return <Spinner label="Loading your next workout" />;
  return (
    <>
      <header className="page-heading">
        <p className="eyebrow">Your private workout</p>
        <h1>Ready when you are</h1>
        <p>Complete the next workout in your own A–D rotation.</p>
      </header>
      {error && <ErrorNotice message={error} />}{" "}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      {active && (
        <Card className="active-banner">
          <span className="status-dot status-dot--attention" />{" "}
          <div>
            <strong>Workout in progress</strong>
            <p>Continue exactly where you stopped.</p>
          </div>
          <button
            className="button button--primary"
            onClick={() => onWorkout(active)}
          >
            Resume
          </button>
        </Card>
      )}
      {data && (
        <Card className="hero-card">
          <div className="hero-card__letter" aria-hidden="true">
            {data.template.code}
          </div>
          <div>
            <p className="eyebrow">Next workout</p>
            <h2>{data.template.name}</h2>
            <p>{data.template.subtitle}</p>
            <div className="date-row">
              <span>{formatDate(`${data.scheduledDate}T12:00:00`)}</span>
              <span
                className={data.isScheduledToday ? "pill pill--green" : "pill"}
              >
                {data.isScheduledToday
                  ? "Scheduled today"
                  : "Start now available"}
              </span>
            </div>
          </div>
          <div className="hero-card__actions">
            <button
              className="button button--primary button--large"
              onClick={() => void start(data.template.id, data.scheduledDate)}
              disabled={busy || !!active}
            >
              {busy ? "Starting…" : "Start workout"}
            </button>
            {data.isScheduledToday && (
              <button
                className="button button--secondary"
                onClick={() => void skipToday()}
                disabled={busy || !!active}
              >
                Skip today · keep rotation
              </button>
            )}
          </div>
        </Card>
      )}
      {data?.lastSession && (
        <Card>
          <p className="eyebrow">Last session</p>
          <h2>{data.lastSession.name ?? `Workout ${data.lastSession.code}`}</h2>
          <p>
            {formatDate(data.lastSession.completedAt)} ·{" "}
            {formatDuration(data.lastSession.elapsedSeconds)}
          </p>
          {data.lastSession.templateId && (
            <div className="inline-actions home-workflows">
              <button
                className="button button--secondary"
                onClick={() =>
                  void start(data.lastSession!.templateId!, undefined, false)
                }
                disabled={busy || !!active}
              >
                Repeat last
              </button>
              <button
                className="button button--secondary"
                onClick={() => void useLastAsNext()}
                disabled={busy || !!active}
              >
                Use as next
              </button>
            </div>
          )}
          {!data.lastSession.templateId &&
            data.lastSession.repeatUnavailableReason && (
              <p className="muted small">
                {data.lastSession.repeatUnavailableReason} Its permanent history
                remains available.
              </p>
            )}
        </Card>
      )}
      <Card>
        <h2>Unfinished workouts</h2>
        <p className="muted small">Saved until you resume or close them. There is no expiry.</p>
        {unfinished.filter(row => row.status === 'partial').length ? unfinished.filter(row => row.status === 'partial').map(row =>
          <button key={row.id} className="unfinished-row" onClick={() => setRecordId(row.id)}>
            <strong>{row.template.name}</strong><span>{row.progress.percentage}% · {row.progress.completed}/{row.progress.prescribed} sets</span><span>View / resume ›</span>
          </button>) : <p>No stopped workouts.</p>}
      </Card>
      <Calendar profileId={profileId} timezone={timezone} onWorkout={onWorkout} refresh={revision}/>
      {recordId && <SessionRecord id={recordId} profileId={profileId} onClose={() => setRecordId(null)} onResume={onWorkout}
        onChanged={() => { void load(); setRevision(value => value+1); }}/>} 
    </>
  );
}
